import Coordinate from '../common/coordinate';
import {
    AltitudeUnit,
    DistanceUnitsEnum,
    FlightFobVolumeDisplayUnit,
    FlightFobWeightDisplayUnit,
    LatLongDisplayFormatEnum,
    PlannedSpeedType
} from '../common/enums';
import Fuel from '../common/fuel';
import Altitude from '../common/altitude';
import { UnitSettings } from '../state/settings';
import { Units } from '@turf/turf';
import { FuelDto, FuelUnit } from '@fe/core';

class _UnitFormatter {
    private settings: UnitSettings = {
        fobVolume: FlightFobVolumeDisplayUnit.Gallon,
        fobWeight: FlightFobWeightDisplayUnit.Pound,
        latLngFormat: LatLongDisplayFormatEnum.DegreesAndDecimalMinutes,
        distanceUnit: DistanceUnitsEnum.NauticalMiles
    };

    setProps(settings: UnitSettings) {
        this.settings = settings;
    }

    formatAltitude(input: number | null | undefined): string {
        if (input == null) {
            return '';
        }

        return input.toLocaleString('en-US') + 'ft';
    }

    formatAltitudeObject(altitude: Altitude | null | undefined) {
        if (!altitude) {
            return '';
        }

        return altitude.value.toLocaleString('en-US') + (altitude.unit === AltitudeUnit.Feet ? 'ft' : 'm');
    }

    formatSpeed(input: number | null | undefined, units: PlannedSpeedType | null): string;
    formatSpeed(input: number | null | undefined): string;
    formatSpeed(input: number | null | undefined, units: PlannedSpeedType | null = null): string {
        if (input == null) {
            return '';
        }

        if (units != null) {
            switch (units) {
                case PlannedSpeedType.trueAir:
                    return input.toString() + 'A';
                case PlannedSpeedType.mach:
                    return input.toFixed(2) + 'M';
                case PlannedSpeedType.classified:
                    return 'CLAS';
                case PlannedSpeedType.ground:
                    return input.toString();
                default:
                    return '-';
            }
        }

        return input.toLocaleString('en-US') + 'kts';
    }

    formatLatValue(value: number | null | undefined): string {
        if (value == null) {
            return '';
        }
        return formatLat(value, this.settings.latLngFormat);
    }

    formatLat(cords: Coordinate): string {
        return formatLatCoords(cords, this.settings.latLngFormat);
    }

    formatLatNotamShort(input: number): string {
        const greaterThanZero = input > 0;
        input = Math.abs(input);
        const deg = Math.trunc(input);
        const minFrac = (input - deg) * 60;
        const minutes = Math.trunc(minFrac);
        return `${deg.toFixed(0).padStart(2, '0')}${minutes.toFixed(0).padStart(2, '0')}${greaterThanZero ? 'N' : 'S'}`;
    }

    formatLngNotamShort(input: number): string {
        const greaterThanZero = input > 0;
        input = Math.abs(input);
        const deg = Math.trunc(input);
        const minFrac = (input - deg) * 60;
        const minutes = Math.trunc(minFrac);
        return `${deg.toFixed(0).padStart(2, '0')}${minutes.toFixed(0).padStart(2, '0')}${greaterThanZero ? 'E' : 'W'}`;
    }

    formatLatLngNotamShort(input: [number, number]): string {
        return `${this.formatLatNotamShort(input[1])}/${this.formatLngNotamShort(input[0])}`;
    }

    formatLng(cords: Coordinate): string {
        return formatLngCoord(cords, this.settings.latLngFormat);
    }

    formatLngValue(value: number | null | undefined): string {
        if (value == null) {
            return '';
        }
        return formatLng(value, this.settings.latLngFormat);
    }

    formatLatLng(cords: Coordinate): string {
        return formatLatLng(cords, this.settings.latLngFormat);
    }

    formatFuel(fuel: Fuel | FuelDto | null | undefined, dontShowUnit?: boolean): string {
        if (!fuel) {
            return '';
        }
        const units = this.settings;
        const formattedValue = (value: number, unit: string) =>
            dontShowUnit ? value.toLocaleString('en-US') : value.toLocaleString('en-US') + unit;

        switch (fuel.unit) {
            case FuelUnit.Null:
                return formattedValue(fuel.value, '?');
            case FuelUnit.WeighPounds:
                if (units.fobWeight === FlightFobWeightDisplayUnit.Pound) {
                    return formattedValue(fuel.value, 'lb');
                }
                return formattedValue(Math.round(fuel.value / 2.204622622), 'kg');

            case FuelUnit.WeightKilograms:
                if (units.fobWeight === FlightFobWeightDisplayUnit.Pound) {
                    return formattedValue(Math.round(fuel.value * 2.204622622), 'lb');
                }
                return formattedValue(fuel.value, 'kg');

            case FuelUnit.VolumeGallons:
                if (units.fobVolume === FlightFobVolumeDisplayUnit.Gallon) {
                    return formattedValue(fuel.value, 'gal');
                }
                return formattedValue(Math.round(fuel.value * 3.785411803), 'ltr');

            case FuelUnit.VolumeLiters:
                if (units.fobVolume === FlightFobVolumeDisplayUnit.Gallon) {
                    return formattedValue(Math.round(fuel.value / 3.785411803), 'gal');
                }
                return formattedValue(fuel.value, 'ltr');
        }
    }

    formatDistance(input: number | null) {
        if (input == null) {
            return '';
        }

        switch (this.settings.distanceUnit) {
            case DistanceUnitsEnum.NauticalMiles:
                return Math.round(input).toLocaleString('en-US') + ' NM';
            case DistanceUnitsEnum.StatuteMiles:
                return Math.round(input * 1.151).toLocaleString('en-US') + ' SM';
            case DistanceUnitsEnum.Kilometers:
                return Math.round(input * 1.852).toLocaleString('en-US') + ' KM';
        }
    }

    // Nm to selected unit
    convertDistance(input: number | null): number | null {
        if (input == null) {
            return null;
        }

        switch (this.settings.distanceUnit) {
            case DistanceUnitsEnum.NauticalMiles:
                return input;
            case DistanceUnitsEnum.StatuteMiles:
                return input * 1.151;
            case DistanceUnitsEnum.Kilometers:
                return input * 1.852;
        }
    }

    // Selected unit to Nm
    convertDistanceToNm(input: number): number {
        switch (this.settings.distanceUnit) {
            case DistanceUnitsEnum.NauticalMiles:
                return input;
            case DistanceUnitsEnum.StatuteMiles:
                return input / 1.151;
            case DistanceUnitsEnum.Kilometers:
                return input / 1.852;
        }
    }

    get distanceUnit() {
        switch (this.settings.distanceUnit) {
            case DistanceUnitsEnum.NauticalMiles:
                return 'NM';
            case DistanceUnitsEnum.StatuteMiles:
                return 'SM';
            case DistanceUnitsEnum.Kilometers:
                return 'KM';
            default:
                return '';
        }
    }

    get distanceUnitTurf(): Units {
        switch (this.settings.distanceUnit) {
            case DistanceUnitsEnum.NauticalMiles:
                return 'nauticalmiles';
            case DistanceUnitsEnum.StatuteMiles:
                return 'miles';
            case DistanceUnitsEnum.Kilometers:
                return 'kilometers';
            default:
                return 'nauticalmiles';
        }
    }

    formatDistanceFromFeet(input: number | null) {
        if (input == null) {
            return '';
        }

        switch (this.settings.distanceUnit) {
            case DistanceUnitsEnum.NauticalMiles:
            case DistanceUnitsEnum.StatuteMiles:
                return Math.round(input).toLocaleString('en-US') + ' ft';
            case DistanceUnitsEnum.Kilometers:
                return Math.round(input * 0.3048).toLocaleString('en-US') + ' m';
        }
    }

    formatHeading(input: number | null) {
        return input == null ? '' : Math.round(input).toString();
    }
}

function deg2decMin(input: number, dir: 'NS' | 'EW') {
    const northEast = input > 0;
    input = Math.abs(input);
    const degrees = Math.trunc(input);
    const minute = (input - degrees) * 60;

    return (northEast ? dir[0] : dir[1]) + degrees.toLocaleString('en-US') + ' ' + minute.toFixed(3);
}

function deg2decMinSec(input: number, dir: 'NS' | 'EW') {
    const northEast = input > 0;
    input = Math.abs(input);
    const degrees = Math.trunc(input);
    const minuteFrac = (input - degrees) * 60;
    const minutes = Math.trunc(minuteFrac);
    const seconds = (minuteFrac - minutes) * 60;

    return (
        degrees.toLocaleString('en-US') + ' ' + minutes.toLocaleString('en-US') + ' ' + seconds.toFixed(1) + (northEast ? dir[0] : dir[1])
    );
}

type IUnitFormatter = Omit<_UnitFormatter, 'settings'>;
export const UnitFormatter: IUnitFormatter = new _UnitFormatter();

export function formatLatCoords(value: Coordinate, format: LatLongDisplayFormatEnum): string {
    if (!value.isValid) {
        return '';
    }

    return formatLat(value.lat, format);
}

export function formatLat(value: number, format: LatLongDisplayFormatEnum): string {
    switch (format) {
        case LatLongDisplayFormatEnum.SignedDecimalDegrees:
            return value.toFixed(5);
        case LatLongDisplayFormatEnum.UnsignedDecimalDegrees:
            return Math.abs(value).toFixed(5) + (value > 0 ? 'N' : 'S');
        case LatLongDisplayFormatEnum.DegreesAndDecimalMinutes:
            return deg2decMin(value, 'NS');
        case LatLongDisplayFormatEnum.DegreesMinutesDecimalSeconds:
            return deg2decMinSec(value, 'NS');
    }
}

export function formatLngCoord(value: Coordinate, format: LatLongDisplayFormatEnum): string {
    if (!value.isValid) {
        return '';
    }

    return formatLng(value.lng, format);
}

export function formatLng(value: number, format: LatLongDisplayFormatEnum): string {
    switch (format) {
        case LatLongDisplayFormatEnum.SignedDecimalDegrees:
            return value.toFixed(5);
        case LatLongDisplayFormatEnum.UnsignedDecimalDegrees:
            return Math.abs(value).toFixed(5) + (value > 0 ? 'E' : 'W');
        case LatLongDisplayFormatEnum.DegreesAndDecimalMinutes:
            return deg2decMin(value, 'EW');
        case LatLongDisplayFormatEnum.DegreesMinutesDecimalSeconds:
            return deg2decMinSec(value, 'EW');
    }
}

export function formatLatLng(cords: Coordinate, format: LatLongDisplayFormatEnum): string {
    return cords.isValid ? `${formatLatCoords(cords, format)}, ${formatLngCoord(cords, format)}` : '-';
}
